/*
Copyright 2022-2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  type FC,
  type MouseEvent,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { type Room } from "matrix-js-sdk";
import { Button, Text } from "@vector-im/compound-web";
import {
  LinkIcon,
  CheckIcon,
} from "@vector-im/compound-design-tokens/assets/web/icons";
import copy from "copy-to-clipboard";

import { Modal } from "../Modal";
import { getAbsoluteRoomUrl } from "../utils/matrix";
import styles from "./InviteModal.module.css";
import { Toast } from "../Toast";
import { useRoomEncryptionSystem } from "../e2ee/sharedKeyManagement";
import { QrCode } from "../QrCode";
import { logger } from "matrix-js-sdk/lib/logger";

function deriveGuestBaseUrlFromLocation(): string {
  const { protocol, host } = window.location;

  // Разделяем хост и порт (если есть)
  const [hostname, port] = host.split(":");

  let guestHostname: string;

  if (hostname.startsWith("call-guest.")) {
    // Уже гостевой домен
    guestHostname = hostname;
  } else if (hostname.startsWith("call.")) {
    // call.example.org -> call-guest.example.org
    guestHostname = `call-guest.${hostname.substring("call.".length)}`;
  } else if (hostname.startsWith("guest.")) {
    // guest.example.org оставляем как есть
    guestHostname = hostname;
  } else {
    // По умолчанию добавляем префикс guest.
    guestHostname = `guest.${hostname}`;
  }

  const guestHostWithPort = port ? `${guestHostname}:${port}` : guestHostname;
  return `${protocol}//${guestHostWithPort}`;
}

interface Props {
  room: Room;
  open: boolean;
  onDismiss: () => void;
}

export const InviteModal: FC<Props> = ({ room, open, onDismiss }) => {
  const { t } = useTranslation();
  const e2eeSystem = useRoomEncryptionSystem(room.roomId);

  // Извлекаем домен из roomId для viaServers (формат: !roomId:domain.com)
  // ВАЖНО: всегда добавляем viaServers для удаленных комнат, даже если домен совпадает с клиентом
  // Это необходимо для правильной работы федерации между разными физическими серверами
  const viaServers = useMemo(() => {
    const roomIdParts = room.roomId.split(":");
    if (roomIdParts.length > 1) {
      const roomDomain = roomIdParts[1];
      // Всегда возвращаем домен комнаты как via server
      // Это гарантирует, что ссылки будут работать для гостевых пользователей
      logger.info(`[InviteModal] Extracted viaServers from roomId: ${room.roomId} -> ${roomDomain}`);
      return [roomDomain];
    }
    logger.warn(`[InviteModal] Could not extract domain from roomId: ${room.roomId}`);
    return undefined;
  }, [room.roomId]);

  const url = useMemo(
    () => getAbsoluteRoomUrl(room.roomId, e2eeSystem, room.name, viaServers),
    [e2eeSystem, room.name, room.roomId, viaServers],
  );

  const guestUrl = useMemo(
    () =>
      getAbsoluteRoomUrl(
        room.roomId,
        e2eeSystem,
        room.name,
        viaServers, // Передаём viaServers для гостевой ссылки
        deriveGuestBaseUrlFromLocation(),
      ),
    [e2eeSystem, room.name, room.roomId, viaServers],
  );

  const [toastOpen, setToastOpen] = useState(false);
  const onToastDismiss = useCallback(() => setToastOpen(false), [setToastOpen]);

  const onButtonClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      copy(url);
      onDismiss();
      setToastOpen(true);
    },
    [url, onDismiss],
  );

  const onGuestButtonClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      copy(guestUrl);
      onDismiss();
      setToastOpen(true);
    },
    [guestUrl, onDismiss],
  );

  return (
    <>
      <Modal title={t("invite_modal.title")} open={open} onDismiss={onDismiss}>
        <QrCode className={styles.qrCode} data={url} />
        <Text className={styles.url} size="sm" weight="semibold">
          {url}
        </Text>
        <Button
          className={styles.button}
          Icon={LinkIcon}
          onClick={onButtonClick}
          data-testid="modal_inviteLink"
        >
          {t("action.copy_link")}
        </Button>
        <Button
          className={styles.button}
          Icon={LinkIcon}
          onClick={onGuestButtonClick}
          data-testid="modal_inviteGuestLink"
        >
          Скопировать ссылку для гостя
        </Button>
      </Modal>
      <Toast
        open={toastOpen}
        onDismiss={onToastDismiss}
        autoDismiss={2000}
        Icon={CheckIcon}
      >
        {t("invite_modal.link_copied_toast")}
      </Toast>
    </>
  );
};

